import React, { useEffect, useState, memo, useMemo } from "react"
import Card from "@material-ui/core/Card"
import CardHeader from "@material-ui/core/CardHeader"
import CardContent from "@material-ui/core/CardContent"
import { Doughnut, HorizontalBar } from "react-chartjs-2"
import OTNTRefData from "../../data/completeRefList.json"
import bibleBookList from "../../data/BookChapterList.json"
import SegmentQueries from "../../data/SegmentQueriesArray.json"
import { getTranslation } from "../../scripts/getTranslation"

import { makeStyles } from "@material-ui/core/styles"

const useStyles = makeStyles({
  main: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    textAlign: "center",
    marginBottom: "75px",
    maxHeight: "fit-content",
  },
  card: {
    maxHeight: "500px",
    height: "fit-content",
    margin: "5px",
  },
  leftCard: {
    marginRight: "8px",
  },
  top5: {
    flex: 1,
    textAlign: "center",
    fontSize: "75%",
    padding: 0,
  },
  cardTitle: {
    margin: "0",
    textDecoration: "underline",
    fontWeight: "bold",
    fontSize: "80%",
    padding: "2px",
  },
  top5List: {
    padding: "1px",
    margin: 1,
  },
  cardContent: {
    margin: 0,
    fontSize: "11px",
    lineHeight: "13px",
    position: "relative",
    width: "100%",
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  backgroundColor: {
    backgroundColor: "white",
    border: "solid grey 1px",
  },
  red: {
    color: "#d60000",
  },
  green: {
    color: "#006400",
  },
  blue: {
    color: "darkblue",
  },
  topSources: {
    flex: "1 100px",
  },
  topWords: {
    flex: "1 100px",
  },
  colorComparison: {
    flex: "1 1 300px",
    maxHeight: "275px",
  },
  segmentBarChart: {
    flex: "1 1 400px",
    height: "fit-content",
  },
  chartholder: {
    position: "relative",
    width: "100%",
    height: "100%",
    // padding: '5px',
    margin: 0,
    padding: 5,
    // marginBottom: '50px'
  },
  bookChart: {
    height: "500px",
  },
  segChart: {
    height: "400px",
  },
  piechart: {
    height: "250px",
  },
  introSubtitle: {
    color: "rgb(83, 152, 255)",
    fontWeight: "bold",
  },
})

const getVersesForSegment = segmentData => {
  const modifiedSegData = {
    starting_chapter: parseInt(segmentData.starting_chapter),
    ending_chapter: parseInt(segmentData.ending_chapter),
    starting_verse: parseInt(segmentData.starting_verse),
    ending_verse: parseInt(segmentData.ending_verse),
  }
  const chapterDiff =
    modifiedSegData.ending_chapter - modifiedSegData.starting_chapter

  const segment = segmentData.segment
  const verses = Object.values(OTNTRefData)

  const segVerses = verses.filter(verse => {
    verse.chapter = parseInt(verse.chapter)
    verse.verse = parseInt(verse.verse)

    // if it's S096
    if (segment === "S096" && verse.book === "1Sa") {
      if (verse.chapter === 31) {
        return true
      }
    } else if (segment === "S096" && verse.book === "2Sa") {
      if (verse.chapter === 1) {
        return true
      } else if (verse.chapter === 2 && verse.verse <= 7) {
        return true
      }
    }
    // if it's S115
    else if (segment === "S115" && verse.book === "1Ki") {
      if (verse.chapter === 22 && verse.verse >= 41) {
        return true
      }
    } else if (segment === "S115" && verse.book === "2Ki") {
      if (verse.chapter === 1 || verse.chapter === 2) {
        return true
      } else if (verse.chapter === 3 && verse.verse <= 27) {
        return true
      }
    }

    // if it's a single chapter segment
    else if (
      chapterDiff === 0 &&
      verse.book === segmentData.starting_book &&
      verse.chapter === modifiedSegData.starting_chapter
    ) {
      if (
        verse.verse >= modifiedSegData.starting_verse &&
        verse.verse <= modifiedSegData.ending_verse
      ) {
        return true
      }
    }

    // if it's a 2 chapter segment
    else if (
      chapterDiff === 1 &&
      verse.book === segmentData.starting_book &&
      verse.chapter >= modifiedSegData.starting_chapter &&
      verse.chapter <= modifiedSegData.ending_chapter
    ) {
      if (
        verse.chapter === modifiedSegData.starting_chapter &&
        verse.verse >= modifiedSegData.starting_verse
      ) {
        return true
      } else if (
        verse.chapter === modifiedSegData.ending_chapter &&
        verse.verse <= modifiedSegData.ending_verse
      ) {
        return true
      }
    }

    // if it's a many chapter segment
    else if (
      chapterDiff > 1 &&
      verse.book === segmentData.starting_book &&
      verse.chapter >= modifiedSegData.starting_chapter &&
      verse.chapter <= modifiedSegData.ending_chapter
    ) {
      if (
        verse.chapter === modifiedSegData.starting_chapter &&
        verse.verse >= modifiedSegData.starting_verse
      ) {
        return true
      } else if (
        verse.chapter === modifiedSegData.ending_chapter &&
        verse.verse <= modifiedSegData.ending_verse
      ) {
        return true
      } else if (
        verse.chapter > modifiedSegData.starting_chapter &&
        verse.chapter < modifiedSegData.ending_chapter
      ) {
        return true
      }
    }
    return false
  })
  return segVerses
}

const OTNTData = memo(({ translations, language, segmentID }) => {
  const classes = useStyles()
  const [verses, setVerses] = useState()
  const [segmentRefData, setSegmentRefData] = useState()

  useEffect(() => {
    setSegmentRefData(
      SegmentQueries.filter(seg => seg.segment === segmentID)[0]
    )
    if (segmentRefData) {
      setVerses(getVersesForSegment(segmentRefData))
    }
  }, [segmentID, segmentRefData])

  // ---------------------- //
  // PIE CHART DATA
  // ---------------------- //

  const createPieChartData = () => {
    const totalVerses = segmentRefData.verseCount
      ? segmentRefData.verseCount
      : 0
    const quotations = verses.filter(x => x.quotations.length > 0).length
    const allusions = verses.length - quotations
    const finalObj = {
      verses: totalVerses,
      original: totalVerses !== 0 ? totalVerses - quotations - allusions : 0,
      quotes: quotations,
      allusions: allusions,
    }

    return {
      labels: [
        `${getTranslation(
          translations,

          language,
          "UI",
          "otnt_quotations"
        )}`,
        `${getTranslation(
          translations,

          language,
          "UI",
          "otnt_allusions"
        )}`,
        `${getTranslation(
          translations,

          language,
          "UI",
          "otnt_remainder"
        )}`,
      ],
      datasets: [
        {
          data: [finalObj.quotes, finalObj.allusions, finalObj.original],
          backgroundColor: [
            "rgb(209, 0, 209)",
            "rgb(255, 238, 4)",
            "lightgrey",
          ],
          hoverBackgroundColor: ["purple", "rgb(184, 171, 0)", "grey"],
        },
      ],
      options: {
        textDirection: language === "ARB" ? "rtl" : "ltr",
      },
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 0,
        bottom: 50,
      },
    },
    legend: {
      position: "top",
      labels: {
        fontSize: 9,
        boxWidth: 40,
      },
      // display: false
    },
    options: {
      textDirection: language === "ARB" ? "rtl" : "ltr",
    },
  }

  // ---------------------- //
  // BOOK BAR CHART DATA
  // ---------------------- //
  const BookBarChartOptions = {
    scales: {
      xAxes: [
        {
          stacked: true,
          ticks: {
            stepSize: 1,
          },
        },
      ],
      yAxes: [{ stacked: true }],
    },
    maintainAspectRatio: false,
    legend: {
      position: "right",
      labels: {
        fontSize: 9,
        boxWidth: 20,
        direction: language === "ARB" ? "rtl" : "ltr",
      },
      display: false,
    },
    layout: {
      // padding: {
      //   top: 0,
      //   bottom: 20
      // }
    },
    options: {
      textDirection: language === "ARB" ? "rtl" : "ltr",
    },
  }

  const createBookBarDataObj = () => {
    let quotations = []
    let allusions = []
    verses.forEach(verse => {
      if (quotations.length < 1 && verse.quotations.length > 0) {
        quotations = [...verse.quotations]
      } else if (verse.quotations.length > 0) {
        quotations = [...quotations, ...verse.quotations]
      }
      if (allusions.length < 1 && verse.allusions.length > 0) {
        allusions = [...verse.allusions]
      } else if (verse.allusions.length > 0) {
        allusions = [...allusions, ...verse.allusions]
      }
    })

    let refObj = {}

    const allVerses = Object.values(OTNTRefData)
    quotations.forEach(quotation => {
      const quantity = allVerses.filter(
        x => x.originalRef.indexOf(quotation) !== -1
      )
      quantity.forEach(ref => {
        if (!refObj[ref.book]) {
          refObj[ref.book] = {
            quotations: [ref.fullRef],
            allusions: [],
          }
        } else {
          if (refObj[ref.book].quotations.indexOf(ref.fullRef) === -1) {
            refObj[ref.book].quotations.push(ref.fullRef)
          }
        }
      })
    })

    allusions.forEach(allusion => {
      const quantity = allVerses.filter(
        x => x.originalRef.indexOf(allusion) !== -1
      )
      quantity.forEach(ref => {
        if (!refObj[ref.book]) {
          refObj[ref.book] = {
            quotations: [],
            allusions: [ref.fullRef],
          }
        } else {
          if (refObj[ref.book].allusions.indexOf(ref.fullRef) === -1) {
            refObj[ref.book].allusions.push(ref.fullRef)
          }
        }
      })
    })

    const booksInOrder = Object.keys(bibleBookList)

    let bookData = {
      labels: [],
      quotes: [],
      allusions: [],
    }

    booksInOrder.forEach(book => {
      if (refObj[book]) {
        bookData.labels.push(
          getTranslation(translations, language, "Books", `abbrev_${book}`)
        )
        bookData.quotes.push(refObj[book].quotations.length)
        bookData.allusions.push(refObj[book].allusions.length)
      }
    })

    return {
      labels: bookData.labels,
      datasets: [
        {
          label: "Quotations",
          data: bookData.quotes,
          backgroundColor: "rgb(209, 0, 209)",
          borderColor: "purple",
          borderWidth: 1,
          hoverBackgroundColor: "purple",
          hoverBorderColor: "purple",
        },
        {
          label: "Allusions",
          data: bookData.allusions,
          backgroundColor: "rgb(255, 238, 4)",
          borderColor: "rgb(184, 171, 0)",
          borderWidth: 1,
          hoverBackgroundColor: "rgb(184, 171, 0)",
          hoverBorderColor: "rgb(184, 171, 0)",
        },
      ],
      options: {
        direction: language === "ARB" ? "rtl" : "ltr",
      },
    }
  }

  return (
    <div
      className={classes.root}
      style={language === "ARB" ? { direction: "rtl" } : {}}
    >
      {segmentRefData && verses ? (
        <React.Fragment>
          <p className={classes.introSubtitle}>
            {getTranslation(
              translations,
              language,
              "UI",
              "otnt_segment_data_title"
            )}
          </p>
          <div className={classes.main}>
            <Card
              className={`${classes.backgroundColor} ${classes.card} ${
                classes.colorComparison
              }`}
            >
              <CardHeader
                title={getTranslation(
                  translations,
                  language,
                  "UI",
                  "otnt_segment_piechart_title"
                )}
                disableTypography={true}
                className={classes.cardTitle}
              />
              <CardContent
                className={`${classes.cardContent} ${classes.piechart}`}
              >
                <div className={classes.chartholder}>
                  <Doughnut
                    data={createPieChartData()}
                    options={chartOptions}
                    height={null}
                    width={null}
                  />
                </div>
              </CardContent>
            </Card>
            <Card
              className={`${classes.top5} ${classes.leftCard} ${
                classes.backgroundColor
              } ${classes.card}  ${classes.segmentBarChart}`}
            >
              <CardHeader
                title={getTranslation(
                  translations,
                  language,
                  "UI",
                  "otnt_segment_barchart_title"
                )}
                disableTypography={true}
                className={classes.cardTitle}
              />
              <CardContent
                className={`${classes.cardContent}`}
                style={{
                  height: `${createBookBarDataObj(translations, language).labels
                    .length *
                    12 +
                    100}px`,
                }}
              >
                <div className={classes.chartholder}>
                  <HorizontalBar
                    data={createBookBarDataObj(translations, language)}
                    width={null}
                    height={null}
                    options={BookBarChartOptions}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </React.Fragment>
      ) : null}
    </div>
  )
})

export default OTNTData
